/**
 * ResearchRpcClient - AtomRpc integration for research execution endpoints
 *
 * This uses AtomRpc.Tag to create a special context tag that provides
 * proper integration with the atom runtime.
 *
 * Uses NDJSON serialization to support streaming responses for
 * real-time research execution progress.
 *
 * The ClerkTokenGetter layer reads the `__session` cookie directly (see
 * `readClerkSessionCookieToken`), falling back to `globalThis.__clerkSession`
 * when the cookie isn't present.
 *
 * @example
 * ```typescript
 * const executeResearchAtom = ResearchRpcClient.runtime.fn(
 *   Effect.fnUntraced(function*(params: { query: string }) {
 *     const client = yield* ResearchRpcClient;
 *     // Stream is returned for processing events
 *     const stream = client["research.execute"](params);
 *     yield* Stream.runForEach(stream, (event) => {
 *       console.log(event);
 *       return Effect.void;
 *     });
 *   })
 * );
 * ```
 *
 * @since 1.0.0
 */
import { AtomRpc } from "@effect-atom/atom-react";
import { FetchHttpClient } from "@effect/platform";
import { RpcSerialization } from "@effect/rpc";
import { ResearchRpcs } from "@fm/api/rpc";
import { Layer } from "effect";
import { makeRpcClientLayer, makeTokenGetterLayer } from "./shared.ts";

export class ResearchRpcClient extends AtomRpc.Tag<ResearchRpcClient>()(
  "ResearchRpcClient",
  {
    group: ResearchRpcs,
    protocol: makeRpcClientLayer(ResearchRpcs).pipe(
      Layer.provide(RpcSerialization.layerNdjson),
      Layer.provide(makeTokenGetterLayer()),
      Layer.provide(FetchHttpClient.layer),
    ),
  },
) {}
